import is from '@sindresorhus/is';
import type { FunctionComponent, ReactNode } from 'react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';

import { useTileErrorBoundary, useTileSlot, useTilingEngine } from './TilingEngineContext';

export type TilingEnginePortalProps = {
    readonly children: ReactNode;
    readonly id: string;
    readonly maxWidth?: string;
    readonly minWidth?: string;
    readonly pinRight?: boolean;
    readonly width?: string;
};

export const TilingEnginePortal: FunctionComponent<TilingEnginePortalProps> = ({
    children,
    id,
    maxWidth,
    minWidth,
    pinRight,
    width,
}) => {
    const { registerTile, updateTile } = useTilingEngine();
    const slot = useTileSlot(id);
    const ErrorBoundary = useTileErrorBoundary();

    // biome-ignore lint/correctness/useExhaustiveDependencies: width/min/max are intentionally synced through updateTile so we don't unregister and reregister on every prop change
    useEffect(() => {
        return registerTile({ id, maxWidth, minWidth, pinRight, width });
    }, [id, pinRight, registerTile]);

    useEffect(() => {
        updateTile(id, { maxWidth, minWidth, width });
    }, [id, maxWidth, minWidth, updateTile, width]);

    if (is.null(slot)) {
        return null;
    }

    const portalChildren = is.nullOrUndefined(ErrorBoundary) ? (
        children
    ) : (
        <ErrorBoundary tileId={id}>{children}</ErrorBoundary>
    );

    return createPortal(portalChildren, slot);
};
